// API base URL - change this to your backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL
// const API_BASE_URL = "http://localhost:3001/api"

/**
 * Admin login API call
 * @param {string} email - Admin email
 * @param {string} password - Admin password
 * @returns {Promise<Object>} - Response with admin data and token
 */
export const adminLogin = async (email, password) => {
  const response = await fetch(`${API_BASE_URL}/admin/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.message || "Login failed")
  }

  return data
}

/**
 * Store auth data in localStorage
 * @param {Object} admin - Admin object
 * @param {string} token - JWT token
 */
export const setAuthData = (admin, token) => {
  localStorage.setItem("admin", JSON.stringify(admin))
  localStorage.setItem("token", token)
}

/**
 * Get stored token
 * @returns {string|null} - JWT token or null
 */
export const getToken = () => {
  return localStorage.getItem("token")
}

/**
 * Get stored admin data
 * @returns {Object|null} - Admin object or null
 */
export const getAdmin = () => {
  const admin = localStorage.getItem("admin")
  return admin ? JSON.parse(admin) : null
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getToken()
}

/**
 * Logout - clear auth data
 */
export const logout = () => {
  localStorage.removeItem("admin")
  localStorage.removeItem("token")
}

/**
 * Get admin profile from API
 * @returns {Promise<Object>} - Response with admin profile data
 */
export const getAdminProfile = async () => {
  const token = getToken()

  if (!token) {
    throw new Error("No token found")
  }

  const response = await fetch(`${API_BASE_URL}/admin/profile`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  const data = await response.json()

  // Handle token expiration
  if (!data.success && data.message?.includes("Invalid or expired token")) {
    logout()
    window.location.href = "/admin/login"
    throw new Error("Session expired")
  }

  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch profile")
  }

  return data
}

/**
 * Update admin profile
 * @param {Object} profileData - Profile data to update (name, profileImage)
 * @returns {Promise<Object>} - Response with updated admin profile data
 */
export const updateAdminProfile = async (profileData) => {
  const token = getToken()

  if (!token) {
    throw new Error("No token found")
  }

  const formData = new FormData()

  if (profileData.name) {
    formData.append("name", profileData.name)
  }

  if (profileData.profileImage) {
    formData.append("profileImage", profileData.profileImage)
  }

  const response = await fetch(`${API_BASE_URL}/admin/profile`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  })

  const data = await response.json()

  // Handle token expiration
  if (!data.success && data.message?.includes("Invalid or expired token")) {
    logout()
    window.location.href = "/admin/login"
    throw new Error("Session expired")
  }

  if (!response.ok) {
    throw new Error(data.message || "Failed to update profile")
  }

  return data
}
